import React, { useEffect, useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import "./css/fixed.css";
import { FaFacebookF, FaInstagram, FaArrowUp, FaLinkedinIn } from "react-icons/fa";
import { Link } from "react-router-dom";
import GoogleCloudPartnerBadge from "../assets/imgs/partners/GC-Partner-no_outline-H.webp";
import RazorpayPartnerBadge from "../assets/imgs/partners/Razorpay-Partner.png";
import ShopifyStorePartner from "../assets/imgs/partners/shopify-partner.jpg";
import { db } from "../apis/fb";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

export default function Footer() {
  const sliderRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef(null);
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState({ type: "", text: "" });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [formStartTime, setFormStartTime] = useState(Date.now());

  useEffect(() => {
    let animationFrame;
    const slider = sliderRef.current;
    if (!slider) return;

    const scroll = () => {
      if (slider) {
        slider.scrollLeft += 1;
        if (slider.scrollLeft >= slider.scrollWidth / 2) {
          slider.scrollLeft = 0;
        }
      }
      animationFrame = requestAnimationFrame(scroll);
    };

    animationFrame = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(normalizedEmail)) {
      setSubscriptionMessage({
        type: "error",
        text: "Please enter a valid email address."
      });

      return;
    }

    // Honeypot check
    if (honeypot) {
      return;
    }

    // Prevent extremely fast submissions
    const timeTaken = Date.now() - formStartTime;

    if (timeTaken < 3000) {
      setSubscriptionMessage({
        type: "error",
        text: "Please wait a moment before submitting."
      });

      return;
    }

    // Turnstile check
    if (!turnstileToken) {
      setSubscriptionMessage({
        type: "error",
        text: "Please complete the security verification."
      });

      return;
    }

    setIsSubscribing(true);

    setSubscriptionMessage({
      type: "",
      text: ""
    });

    try {
      const response = await fetch(
        "/api/send-subscription-email",
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json"
          },

          body: JSON.stringify({
            email: normalizedEmail,
            name: normalizedEmail.split("@")[0],
            turnstileToken: turnstileToken,
            website: honeypot,
            formStartedAt: formStartTime
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.error || "Failed to subscribe"
        );
      }

      const subscribersRef = collection(
        db,
        "mailsubscribers"
      );

      await addDoc(subscribersRef, {
        email: normalizedEmail,
        subscribedAt: serverTimestamp(),
        status: "active",
        source: "footer_subscription"
      });

      setSubscriptionMessage({
        type: "success",
        text:
          "Thank you for subscribing! Welcome to RhythmGrowthLab."
      });

      setEmail("");
      setTurnstileToken("");
      setHoneypot("");
      setFormStartTime(Date.now());

    } catch (error) {

      console.error(
        "Subscription error:",
        error
      );

      setSubscriptionMessage({
        type: "error",

        text:
          error.message ||
          "Something went wrong. Please try again later."
      });

    } finally {

      setIsSubscribing(false);

      setTimeout(() => {
        setSubscriptionMessage({
          type: "",
          text: ""
        });
      }, 5000);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="getintouch" ref={footerRef}>
      <div className="getintouch-title-wrapper">
        <div className="getintouch-title-slider" ref={sliderRef}>
          <div className="title-track">
            <h1 className="getintouch-title">
              Get In Touch<span> </span>Get In Touch
            </h1>
            <h1 className="getintouch-title">
              Get In Touch<span> </span>Get In Touch
            </h1>
          </div>
        </div>
      </div>

      <div className={`getintouch-content ${isVisible ? 'animate' : ''}`}>
        <div className="footer-col brand-col fade-in-left">
          <h3 className="brand">RhythmGrowthLab</h3>
          <p className="brand-desc">
            Expert website development, mobile apps, branding, SEO & performance marketing for scalable, high-converting growth..
          </p>

          <form onSubmit={handleSubscribe} className="subscribe-form">
            <div className="subscribe">
              <input 
                type="email" 
                placeholder="Email Address" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubscribing}
                required
              />

              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) =>
                  setHoneypot(e.target.value)
                }
                autoComplete="off"
                tabIndex="-1"
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  opacity: 0,
                  pointerEvents: "none"
                }}
              />

              <button 
                type="submit" 
                className="subscribe-btn" 
                disabled={isSubscribing}
              >
                {isSubscribing ? "Subscribing..." : "Subscribe"}
              </button>
            </div>

            {subscriptionMessage.text && (
              <div className={`subscription-message ${subscriptionMessage.type}`}>
                {subscriptionMessage.text}
              </div>
            )}
          </form>

          <div className="turnstile-container">
            <Turnstile
              siteKey="0x4AAAAAAEpbKlkijrWYf8i_"
              onSuccess={(token) => {
                setTurnstileToken(token);
              }}
              onExpire={() => {
                setTurnstileToken("");
              }}
              onError={() => {
                setTurnstileToken("");
              }}
            />
          </div>
        </div>

        <div className="footer-col fade-in-up">
          <h4>Quick Links</h4>
          <ul className="footer-links">
            <li><Link to="/" onClick={() => scrollToSection('home')}>Home</Link></li>
            <li><Link to="/services-and-pricing">Our Services & Pricings</Link></li>
            <li><Link to="/our-portfolio">Our Portfolio</Link></li>
            <li><Link to="/agency-account">Agency Account</Link></li>
            <li><Link to="/contact-us">Contact Us</Link></li>
          </ul>
        </div>

        <div className="footer-col fade-in-right">
          <h4>Contact Us</h4>
          <div className="contact-info">
            <p>
              <span className="contact-label">Email:</span>
              <a href="mailto:rhythmgrowthlab@gmail.com">rhythmgrowthlab@gmail.com</a>
            </p>
            <p>
              <span className="contact-label">Phone:</span>
              <a href="tel:+919582795505">+91-9582795505</a>
            </p>
          </div>
        </div>

        <div className="footer-col fade-in-up">
          <h4>Follow Us</h4>
          <div className="socials">
            <a href="https://www.instagram.com/rhythmgrowthlab" target="_blank" rel="noopener noreferrer" className="social-icon">
              <FaInstagram />
            </a>
            <a href="https://www.facebook.com/people/Rhythmgrowthlab/61587331876796/" target="_blank" rel="noopener noreferrer" className="social-icon">
              <FaFacebookF />
            </a>
            <a href="https://www.linkedin.com/company/rhythmgrowthlab" target="_blank" rel="noopener noreferrer" className="social-icon">
              <FaLinkedinIn />
            </a>
          </div>

          <div className="partner-badges">
            <h5 className="partner-badges-title">Official Business Partners</h5>
            <div className="badges-container">
              <div className="partner-badge">
                <img 
                  src={GoogleCloudPartnerBadge}
                  alt="Google Cloud Partner"
                  className="badge-icon"
                />
              </div>
              <div className="partner-badge">
                <img 
                  src={RazorpayPartnerBadge}
                  alt="Razorpay Partner"
                  className="badge-icon"
                />
              </div>
              <div className="partner-badge">
                <img 
                  src={ShopifyStorePartner}
                  alt="Shopify Partner"
                  className="badge-icon"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`footer-bottom ${isVisible ? 'animate' : ''}`}>
        <p className="copyright">
          Copyright © 2025-{new Date().getFullYear()} RhythmGrowthLab. All rights reserved.
        </p>

        <button className="scroll-top" onClick={scrollToTop} aria-label="Scroll to top">
          <FaArrowUp />
        </button>

        <div className="links">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-of-service">Terms of Service</Link>
          <Link to="/refund-policy">Refund Policy</Link>
        </div>
      </div>
    </section>
  );
}